import { BaseEntity } from './common';

/**
 * Research Types
 * Types of genealogy research activities
 */
export enum ResearchType {
  VITAL_RECORDS = 'vital-records',
  CENSUS = 'census',
  MILITARY = 'military',
  IMMIGRATION = 'immigration',
  LAND_PROPERTY = 'land-property',
  PROBATE = 'probate',
  CHURCH_RECORDS = 'church-records',
  NEWSPAPER = 'newspaper',
  DNA = 'dna',
  PHOTOGRAPHS = 'photographs',
  ORAL_HISTORY = 'oral-history',
  FAMILY_BIBLE = 'family-bible',
  CORRESPONDENCE = 'correspondence',
  OTHER = 'other'
}

/**
 * Research Status
 * Status of research progress
 */
export enum ResearchStatus {
  NOT_STARTED = 'not-started',
  IN_PROGRESS = 'in-progress',
  BLOCKED = 'blocked',
  COMPLETED = 'completed',
  ON_HOLD = 'on-hold'
}

/**
 * Research Priority
 * Priority level for research tasks
 */
export enum Priority {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  CRITICAL = 'critical'
}

/**
 * Research Note approval status
 */
export type ResearchNoteApprovalStatus = 'pending' | 'approved' | 'rejected';

/**
 * Research Note entity
 * Represents a research note for tracking genealogy research
 */
export interface ResearchNote extends BaseEntity {
  // Association
  personId: string;                    // Person being researched
  personName: string;                  // Cached for performance

  // Content
  title: string;                       // Research note title
  objective: string;                   // What you're trying to find/prove
  findings?: string;                   // Research findings (rich text/markdown)
  hypothesis?: string;                 // Working hypothesis

  // Classification
  type: ResearchType;                  // Type of research
  status: ResearchStatus;              // Current status
  priority: Priority;                  // Priority level

  // Tasks
  tasks: ResearchTask[];               // Checklist of tasks

  // Sources
  sources: SourceCitation[];           // Source citations

  // Documents
  linkedDocumentIds: string[];         // IDs of related documents

  // Collaboration
  isPrivate: boolean;                  // Private (only author) or shared with editors
  assignedTo: string[];                // User IDs assigned to this research
  assignedToNames: string[];           // Cached for performance

  // Metadata
  researcherId: string;                // Who created the research note
  researcherName: string;              // Cached for performance
  lastUpdatedBy?: string;              // Last person to update
  lastUpdatedByName?: string;          // Cached for performance

  // Progress tracking
  completionPercentage: number;        // 0-100 based on tasks completed
  lastActivityAt?: Date;               // Last time any update was made

  // Moderation (only for shared notes)
  approvalStatus?: ResearchNoteApprovalStatus; // Only relevant for shared notes
  isApproved: boolean;                 // Quick check (always true for private)
  approvedBy?: string;                 // Admin who approved
  approvedAt?: Date;                   // When approved
  rejectionReason?: string;            // Reason for rejection

  // Soft delete
  isDeleted: boolean;                  // Soft delete flag
  deletedBy?: string;                  // Who deleted it
  deletedAt?: Date;                    // When deleted
}

/**
 * Research Task
 * Individual task within a research note
 */
export interface ResearchTask {
  id: string;                          // Unique task ID
  description: string;                 // Task description
  isCompleted: boolean;                // Completion status
  completedAt?: Date;                  // When completed
  completedBy?: string;                // Who completed it
  notes?: string;                      // Notes about this task
  dueDate?: string;                    // Optional due date (ISO format)
}

/**
 * Source Citation
 * Citation for a research source
 */
export interface SourceCitation {
  id: string;                          // Unique citation ID
  sourceType: 'book' | 'website' | 'document' | 'database' | 'interview' | 'other';
  title: string;                       // Source title
  author?: string;                     // Author/creator
  publisher?: string;                  // Publisher
  publicationDate?: string;            // Publication date
  url?: string;                        // URL (for online sources)
  accessedDate?: string;               // Date accessed (for online sources)
  repositoryName?: string;             // Repository/archive name
  repositoryLocation?: string;         // Repository location
  callNumber?: string;                 // Call number or reference
  pages?: string;                      // Page numbers
  notes?: string;                      // Additional notes
  formattedCitation?: string;          // Formatted citation (auto-generated)
}

/**
 * Research Note form data
 */
export interface ResearchNoteFormData {
  personId: string;
  title: string;
  objective: string;
  findings?: string;
  hypothesis?: string;
  type: ResearchType;
  status: ResearchStatus;
  priority: Priority;
  isPrivate: boolean;
  assignedTo?: string[];
}

/**
 * Research Note filter options
 */
export interface ResearchNoteFilters {
  type?: ResearchType;
  status?: ResearchStatus;
  priority?: Priority;
  researcherId?: string;
  assignedTo?: string;
  isPrivate?: boolean;
  approvalStatus?: ResearchNoteApprovalStatus;
  searchQuery?: string;
}

/**
 * Research Note statistics
 */
export interface ResearchNoteStats {
  totalNotes: number;
  notesByStatus: Record<ResearchStatus, number>;
  notesByType: Record<ResearchType, number>;
  notesByPriority: Record<Priority, number>;
  privateNotes: number;
  sharedNotes: number;
  pendingApproval: number;
  averageCompletionPercentage: number;
}

/**
 * Research Task update data
 */
export interface ResearchTaskUpdate {
  taskId: string;
  isCompleted: boolean;
  notes?: string;
}
